import React, { ChangeEvent, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import SimpleBar from 'simplebar-react';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';

import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import DatePickerCustom from 'src/components/common/field/DatePickerCustom';
import InputCustom from 'src/components/common/field/InputCustom';
import InputSearch from 'src/components/common/field/InputSearch';
import DropDown from 'src/components/common/field/DropDown';
import InputTextAreaAutosize from 'src/components/common/field/InputTextAreaAutosize';
import Record from './Record';
import { ItemTypePayment, DataPaymentType } from 'src/components/quotations/TypeCustom';
import { getToken, getUserLogged } from 'src/utils/auth';
import { getAPI, postAPI } from 'src/utils/api';
import Skeleton from '@mui/material/Skeleton';
import { useNotify } from 'src/context/ContextNotify';
import { isEmpty, getField } from 'src/common/helper';

const typographyLabel = {
  fontSize: '14px',
  lineHeight: '16.8px',
  fontWeight: 500,
  width: '90px'
};

const colorRequired = {
  color: '#ED5565',
  fontSize: '14px',
  fontWeight: '500'
};

const inputSX = {
  width: '160px',
  height: '48px'
};

const sxLableDropDown = {
  color: '#434154',
  '&.Mui-focused': { color: '#434154', transform: 'translate(14px, 16px) scale(1)' },
  '&.MuiInputLabel-root': { color: '#434154', transform: 'translate(14px, 16px) scale(1)' }
}

const StackCenter = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
});

const defaultItem = {
  amount: 0,
  categoryid: null,
  detail: '',
  linkid: null,
  paymentmethod: null,
  paymentmethod2: null,
  paymenttype: null,
  qty: null,
  thedate: '',
  unpaid: 0
};

const defaultForm = {
  paymentDate: null,
  amount: null,
  patientName: '',
  patientId: '',
  doctorId: null,
  person: null,
  note: ''
};

const dataQuoteDefault = {
  categories: [],
  methods: [],
  methods2: [],
  types: [],
  products: [],
  quotes: []
}

const sizeSkeletoField = {
  width: '160px',
  height: '48px'
};

const defaultError = {
  doctorId: true,
  paymentDate: true,
  person: true,
  amount: true
};

const defaultErrorItem = {
  index: -1,
  fieldName: ''
};

type DataFormType = {
  paymentDate: string | null;
  amount: number | null;
  patientName: string;
  patientId: string;
  doctorId: number | null,
  person: number | null,
  note: string
};

interface DialogFormPaymentProps {
  open: boolean;
  infoPayment: any;
  onClose: (isRefreshList?: boolean) => void;
  onSave?: () => void;
};

const DialogFormPayment: React.FC<DialogFormPaymentProps> = (props) => {
  const notifyContext = useNotify();
  const [dataForm, setDataForm] = useState<DataFormType>(defaultForm);
  const [items, setItems] = useState<ItemTypePayment[]>([defaultItem]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [dataPayment, setDataPayment] = useState<DataPaymentType>(dataQuoteDefault);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState(defaultError);
  const [errorsItems, setErrorsItems] = useState(defaultErrorItem);

  const handleChangeDataItem = (index: number, fieldName: string, value: string | number | null) => {
    const categoryCurrent = dataPayment.categories.find(item => item.value && item.value === Number(items[index].categoryid));

    let valueSet: any = {};
    if (fieldName === 'categoryid') {
      const category = dataPayment.categories.find(item => item.value && item.value === Number(value));

      if (category && categoryCurrent && categoryCurrent.code !== category.code)
        valueSet = {
          amount: null,
          linkid: null,
          qty: null,
          detail: ''
        };
    }

    if (fieldName === 'linkid' && categoryCurrent.code === 'product') {
      const product = dataPayment.products.find(item => item.id && Number(item.id) === Number(value));

      valueSet = {
        amount: product && product.price ? product.price : null,
        qty: product ? 1 : null
      };
    }

    if (fieldName === 'qty' && items[index]) {
      const itemIndex = items[index];

      const product = dataPayment.products.find(item => item.id && Number(item.id) === Number(itemIndex.linkid));

      valueSet = {
        amount: product && product.price ? Number(product.price) * Number(value) : null,
      };
    }

    if (valueSet && valueSet['amount'] !== undefined) {
      let newItems = items;
      newItems[index].amount = valueSet['amount'];
      const sum = handleSumAmount(newItems);
      handleChange('amount')(sum);
    }

    let sumAmount: number = 0;

    setItems((prevItems) => {
      return prevItems.map((item, idx) => {
        if (idx === index) {
          sumAmount += Number(value);
          return {
            ...item,
            [fieldName]: value,
            ...valueSet
          };
        } else {
          sumAmount += Number(item.amount);
        }
        return item;
      });
    });

    if (fieldName === 'amount') {
      handleChange('amount')(sumAmount);
    }
  };

  const handleChange = (name: string) => (value: number | string | number[] | null) => {
    setDataForm(prevState => ({
      ...prevState,
      [name]: value
    }))
  };

  const handleChangeDate = (date: Dayjs | null) => {
    const dateFormat = date ? date.format('YYYY/MM/DD') : null;
    handleChange('paymentDate')(dateFormat);
  };

  const handleChangeInputSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    handleChange(name)(value);
  };

  const handleClose = () => {
    props.onClose();
  };

  const handleClickAddRow = () => {
    setItems((prevState) => [
      ...prevState,
      defaultItem
    ])
  };

  const formatNumber = (number: number | string | null): string | null => {
    return number !== null ? new Intl.NumberFormat('en-US').format(Number(number)) : null;
  };

  const handleSumAmount = (listItem: ItemTypePayment[]) => {
    let sum = 0;
    listItem.forEach((item) => {
      sum += Number(item.amount);
    })

    return sum;
  };

  const handleDeleteRow = (index: number) => {
    let deleteItems = items;

    deleteItems.splice(index, 1);

    const sum = handleSumAmount(deleteItems);
    handleChange('amount')(sum);

    setItems((prevState) => [
      ...deleteItems
    ]);
  };

  const filterItem = () => {
    const newItem = items.filter((item) => item.categoryid !== null);
    return newItem;
  };

  const validate = () => {
    if (isEmpty(dataForm.doctorId)) {
      setErrors({ ...defaultError, doctorId: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '医師を選んでください',
        type: 'error'
      })
      return false;
    }
    if (isEmpty(dataForm.paymentDate)) {
      setErrors({ ...defaultError, paymentDate: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '日付を選択して下さい',
        type: 'error'
      });
      return false;
    }
    if (isEmpty(dataForm.person)) {
      setErrors({ ...defaultError, person: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '担当者をお選びください',
        type: 'error'
      });
      return false;
    }

    setErrors(defaultError);

    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error'
    });

    return true;
  };

  const validateItems = () => {
    let checkError = true;

    items.forEach((item, index) => {

      if (item.categoryid !== null) {

        if (isEmpty(item.linkid)) {
          setErrorsItems({ index, fieldName: 'linkid' });
          notifyContext.setNotifyMessage({
            show: true,
            message: 'コンテンツを選択してください',
            type: 'error'
          })
          checkError = false;
        }

        if (isEmpty(item.paymentmethod)) {
          setErrorsItems({ index, fieldName: 'paymentmethod' });
          notifyContext.setNotifyMessage({
            show: true,
            message: '支払方法を選択してください',
            type: 'error'
          })
          checkError = false;
        }

        if (!isEmpty(item.paymentmethod) && Number(item.paymentmethod) === 2 && isEmpty(item.paymentmethod2)) {
          setErrorsItems({ index, fieldName: 'paymentmethod2' });
          notifyContext.setNotifyMessage({
            show: true,
            message: '支払方法を選択してください',
            type: 'error'
          })
          checkError = false;
        }

        if (isEmpty(item.paymenttype)) {
          setErrorsItems({ index, fieldName: 'paymenttype' });
          notifyContext.setNotifyMessage({
            show: true,
            message: '勘定科目を選択してください',
            type: 'error'
          })
          checkError = false;
        }
      }
    })

    if (checkError) {
      setErrorsItems(defaultErrorItem);

      notifyContext.setNotifyMessage({
        show: false,
        message: '',
        type: 'error'
      });
    }

    return checkError;
  }

  const handleSave = () => {
    if (!validate() || !validateItems()) {
      return;
    }
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const data = {
        amount: dataForm.amount,
        caseid: props.infoPayment.caseid,
        patientid: dataForm.patientId,
        doctorid: dataForm.doctorId,
        paymentdate: dataForm.paymentDate,
        person: dataForm.person,
        note: dataForm.note,
        paymentid: null,
        items: filterItem(),
      };

      postAPI(`/patient/save-payment?&token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
            props.onClose(true);
          }
          if (res.code === 500 || res.code === 501) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error'
            });
          }
        })
        .finally(() => {
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const convertItemType = (data: any[], id: string, name: string, accessories?: string) => {
    const converted = data ? data.map((item: any) => ({
      id: String(item[id]),
      name: item[name],
      accessories: accessories && item[accessories] ? item[accessories] : ''
    })) : [];

    return converted;
  };

  const setDefaultDataForm = (data: any) => {
    const userLogin = getUserLogged();

    let valueSet: any = {};
    if (userLogin && userLogin.user && userLogin.user.id) {
      valueSet.person = userLogin.user.id;
    }

    valueSet.patientId = data.patient ? getField(data.patient, 'patientno', null) : null;
    valueSet.patientName = data.patient ? getField(data.patient, 'name', null) : null;

    setDataForm(prevState => ({
      ...prevState,
      ...valueSet
    }));

  }
  const getPaymentDetail = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      setIsLoading(true);
      getAPI(`/patient/payment?patientid=${props.infoPayment.patientid}
        &paymentid=${props.infoPayment.paymentid}
        &caseid=${props.infoPayment.caseid}&token=${token}`)
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            // Is edit
            if (props.infoPayment.paymentid !== 0) {
              // setDataEdit(res.data);
            } else {
              setDefaultDataForm(res.data)
            }

            setDataPayment((prevState) => ({
              ...prevState,
              categories: getField(res.data, 'categories', []),
              methods: getField(res.data, 'methods', []),
              methods2: getField(res.data, 'methods2', []),
              types: getField(res.data, 'types', []),
              products: getField(res.data, 'products', []),
              quotes: getField(res.data, 'quotes', [])
            }));

            const doctorsRes = getField(res.data, 'doctors', []);
            const usersRes = getField(res.data, 'users', []);

            setDoctors(convertItemType(doctorsRes, 'id', 'name'));
            setUsers(convertItemType(usersRes, 'userid', 'name'));
            setIsLoading(false);
          }
        })
        .finally(() => {
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const copyMonitor = (monitors: any[]) => {
    let listMonitor: ItemTypePayment[] = [];

    const categoryProductid = dataPayment.categories.find((item) => getField(item, 'code', '') === 'product');

    monitors.forEach((item) => {
      if (getField(item, 'type', 1) === 2) {
        listMonitor.push({
          amount: Number(getField(item, 'price', 0)) * Number(getField(item, 'qty', 0)),
          categoryid: getField(categoryProductid, 'value', 0),
          detail: '',
          linkid: getField(getField(item, 'product', {}), 'id', null),
          paymentmethod: null,
          paymentmethod2: null,
          paymenttype: null,
          qty: getField(item, 'qty', 0),
          thedate: '',
          unpaid: 0
        });
      }
    });

    setItems((prevState) => [
      ...prevState,
      ...listMonitor
    ]);
  };

  const getQuoteDetail = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      getAPI(
        `/patient/quote?patientid=${getField(props.infoPayment, 'patientid', 0)}
        &quoteid=${getField(props.infoPayment, 'quoteid', 0)}&token=${token}`
      )
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            const quote = getField(res.data, 'quote', {});
            copyMonitor(getField(quote, 'items', []));
          }

          if (res.code === 403 || res.code === 500) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error',
            });
          }
        })
        .finally(() => { });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handleClickCopyMonutor = () => {
    getQuoteDetail();
  };


  const getQuoteDetailForInit = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }
      const fieldPatientId = getField(props.infoPayment, 'patientid', 0)
      if (fieldPatientId === 0) return;

      getAPI(
        `/patient/quote-view?patientid=${fieldPatientId}&id=${getField(props.infoPayment, 'quoteid', 0)}&token=${token}`
      )
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            const item = {
              amount: 0,
              categoryid: res.data.typeid,
              detail: res.data.title,
              linkid: res.data.id,
              paymentmethod: null,
              paymentmethod2: null,
              paymenttype: null,
              qty: null,
              thedate: '',
              unpaid: 0
            };
            setItems([item]);
          }
        })
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  useEffect(() => {
    getQuoteDetailForInit();
    // eslint-disable-next-line
  }, [props.infoPayment]);

  useEffect(() => {
    if (props.open) {
      getPaymentDetail()
    } else {
      setItems([defaultItem]);
      setDataForm(defaultForm);
      setErrors(defaultError);
    }
    // eslint-disable-next-line
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '1020px',
          m: '0px'
        }
      }}
    >
      <DialogTitle id='scroll-dialog-title' sx={{ p: '24px', pb: '0px' }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography sx={{ fontSize: '20px', lineHeight: '24px', fontWeight: 500 }}>
            入金登録
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close-blue.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: '0px 10px 24px 24px',
          '& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before': {
            backgroundColor: '#CCD1D9',
          },
          overflow: 'hidden'
        }}
      >
        <SimpleBar
          style={{ height: 'calc(100vh - 210px)', borderWidth: '18px' }}
        >
          <Stack spacing='10px' sx={{ pr: '14px' }}>
            <StackCenter>
              <Stack>
                <Typography sx={{ ...typographyLabel }}>
                  患者情報
                  <Typography component='label' {...colorRequired}>
                    *
                  </Typography>
                </Typography>
                <Typography sx={{ ...typographyLabel, fontSize: '12px', fontWeight: 400 }}>
                  (どちらか)
                </Typography>
              </Stack>
              {!isLoading ? (
                <StackCenter>
                  <InputSearch
                    labelInput='患者名'
                    name='patientName'
                    readonly={true}
                    value={dataForm.patientName}
                    onChange={handleChangeInputSearch}
                    sx={{ mr: '10px' }}
                  />
                  <InputSearch
                    labelInput='患者ID'
                    name='patientId'
                    readonly={true}
                    value={dataForm.patientId}
                    onChange={handleChangeInputSearch}
                  />
                </StackCenter>
              ) : (
                <StackCenter>
                  <Skeleton variant='rounded' sx={{ ...sizeSkeletoField, mr: '10px' }} />
                  <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
                </StackCenter>
              )}
            </StackCenter>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                医師
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              {!isLoading ? (
                <DropDown
                  selected={dataForm.doctorId}
                  idSelect='doctor-select'
                  placeholder='選択してください'
                  items={doctors}
                  onChange={handleChange('doctorId')}
                  sxInput={{ ...inputSX }}
                  sxLabel={sxLableDropDown}
                  error={!errors.doctorId}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                予約日
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>

              {!isLoading ? (
                <DatePickerCustom
                  value={dayjs(dataForm.paymentDate)}
                  onChange={handleChangeDate}
                  error={!errors.paymentDate}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                担当者
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              {!isLoading ? (
                <DropDown
                  selected={dataForm.person}
                  idSelect='person-select'
                  placeholder='選択してください'
                  items={users}
                  onChange={handleChange('person')}
                  sxInput={{ ...inputSX }}
                  sxLabel={sxLableDropDown}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                金額
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>

              {!isLoading ? (
                <InputCustom
                  value={formatNumber(dataForm.amount)}
                  type='text'
                  name='amount'
                  labelInput='10,000'
                  readonly={true}
                  onChange={() => { }}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <Box sx={{ m: '15px 0px 5px !important' }}><Divider /></Box>

            <Box sx={{ width: '100%' }}>
              <Record
                items={items}
                dataPayment={dataPayment}
                loading={isLoading}
                onChangeItem={handleChangeDataItem}
                onClickAddRow={handleClickAddRow}
                onClickDelete={handleDeleteRow}
                onClickCopyMonitor={handleClickCopyMonutor}
                error={errorsItems}
                patientId={dataForm.patientId}
              />
            </Box>

            <Box sx={{ m: '15px 0px 5px !important' }}><Divider /></Box>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                備考
              </Typography>

              <InputTextAreaAutosize
                value={dataForm.note}
                onChange={handleChange('note')}
                width='100%'
                height='104px'
                m='0px 0px 0px 10px'
              />
            </StackCenter>

          </Stack>
        </SimpleBar>
      </DialogContent>

      <DialogActions
        style={{
          justifyContent: 'right',
          padding: '16px'
        }}
      >
        <BtnOutlineGradient text='キャンセル' handleButtonClick={handleClose} disabled={isLoading} />
        <BtnGradient text='登録' handleButtonClick={handleSave} disabled={isLoading} />
      </DialogActions>
    </Dialog>
  );
};

export default DialogFormPayment;
